import { Link, useRouterState } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCurrentWorkspace } from "~/api/queries";
import { Menu, type MenuItem } from "~/foundation/components/Menu";
import { Tooltip } from "~/foundation/components/Tooltip";
import { cn } from "~/foundation/utils/cn";
import { activeDestination, type NavDestination, navCatalog } from "~/shell/navCatalog";
import { useShellStore } from "~/shell/store";

/** Chips shown inline; the rest fold into a "+n" menu so the bar never wraps. */
const MAX_VISIBLE_PINS = 6;

/**
 * Pinned favourites as a bookmarks strip beside the section switcher (layered nav,
 * 2026-09-09). The same pins as the launcher's "Pinned" group and the sidebar stars.
 */
export function PinnedBar() {
  const { t } = useTranslation();
  const { data: workspace } = useCurrentWorkspace();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const pinnedIds = useShellStore((state) => state.pinnedNavIds);

  const abilities = workspace?.abilities ?? {};
  const catalog = useMemo(() => navCatalog(abilities), [abilities]);
  const pinned = useMemo(
    () =>
      pinnedIds
        .map((id) => catalog.find((destination) => destination.id === id))
        .filter((destination): destination is NavDestination => !!destination),
    [pinnedIds, catalog],
  );

  if (pinned.length === 0) {
    return null;
  }

  const active = activeDestination(catalog, pathname);
  const visible = pinned.slice(0, MAX_VISIBLE_PINS);
  const overflow = pinned.slice(MAX_VISIBLE_PINS);
  const overflowItems: MenuItem[] = overflow.map((destination) => ({
    type: "route",
    title: t(destination.title),
    icon: destination.icon,
    to: destination.to,
  }));

  return (
    <nav aria-label={t("Pinned areas")} className="hidden min-w-0 items-center gap-0.5 md:flex">
      <span aria-hidden className="mx-1 h-5 w-px shrink-0 bg-divider" />
      {visible.map((destination) => {
        const Icon = destination.icon;
        const isActive = active?.id === destination.id;
        return (
          <Tooltip key={destination.id} content={t(destination.title)} placement="block-end">
            <Link
              to={destination.to as never}
              aria-current={isActive ? "page" : undefined}
              className={cn(
                "flex h-8 shrink-0 items-center gap-1.5 rounded-sm px-2 text-small font-medium",
                "transition-colors duration-100 ease-in-out motion-reduce:transition-none",
                isActive
                  ? "bg-sidebar-active-bg text-text"
                  : "text-text-secondary hover:bg-hover-bg hover:text-text",
              )}
            >
              <Icon size={16} aria-hidden />
              <span className="hidden max-w-[120px] truncate lg:inline">
                {t(destination.title)}
              </span>
            </Link>
          </Tooltip>
        );
      })}
      {overflow.length > 0 ? (
        <Menu
          ariaLabel={t("Pinned areas")}
          items={overflowItems}
          trigger={
            <button
              type="button"
              aria-label={t("More pinned areas")}
              className="flex h-8 shrink-0 items-center rounded-sm px-2 text-small font-semibold text-text-tertiary hover:bg-hover-bg hover:text-text data-[state=open]:bg-hover-bg data-[state=open]:text-text"
            >
              +{overflow.length}
            </button>
          }
        />
      ) : null}
    </nav>
  );
}
